import { Injectable, NgZone } from '@angular/core';

// external libs
import { Observable, Observer } from 'rxjs';

// services
import { MarkerClusterService } from '../abstract/abstract-marker-cluster';

// directives
import { H21MapMarkerClusterDirective } from '../../directives/h21-map-marker-cluster.directive';

// models
import { FeatureEvent } from '../../models/feature-event.model';
import { Position } from '../../models/position.model';
import { CircleEvent } from '../../models/circle-event.model';
import { MarkerClusterFilter } from '../../models/marker-cluster-filter.model';

@Injectable()
export class MapboxDataLayerService extends MarkerClusterService<L.Map, L.Marker, L.Polyline> {

    private layers: Map<H21MapMarkerClusterDirective, L.GeoJSON> = new Map<H21MapMarkerClusterDirective, L.GeoJSON>();
    private _layer: H21MapMarkerClusterDirective;

    constructor(private _zone: NgZone) { super(); }

    public updateMarkerCluster(layer: H21MapMarkerClusterDirective): void {
        this._removeFeature(layer);
        this.createOverlay(layer);
        this.ready$.next();
        this.getCountLoadMarkers(layer);
    }

    public addMarkerCluster(layer?: H21MapMarkerClusterDirective): void {
        this._createDataLayer().subscribe((dataLayer) => {
            this._layer = layer;
            this.layers.set(layer, dataLayer);
            dataLayer.addTo(this.map.api);
            this.createOverlay(layer);
        });
    }

    public createOverlay(layer?: H21MapMarkerClusterDirective): void {
        if (!layer.geoJson) { return; }
        this.layers.get(layer).addData(layer.geoJson);
      //  this._getLoadMarkers(layer.geoJson);
    }

    public removeMarkerCluster(layer?: H21MapMarkerClusterDirective): void {
        this.layers.get(layer).remove();
    }

    public removeMarkerClusters(): void {
        this.layers.forEach((key) => {
            this.map.api.removeLayer(key);
        });
        this.layers.clear();
    }

    public removeFeature(layer: H21MapMarkerClusterDirective): void {
        this._removeFeature(layer);
    }

    public createEvent<E>(eventName: string, layer: H21MapMarkerClusterDirective): Observable<E> {
        return new Observable((observer: Observer<any>) => {
            this.layers.get(layer) && this.layers.get(layer).addEventListener(eventName, (event) => {
                this._zone.run(() => observer.next(event));
            });
        });
    }

    public createEventMouseOver<D>(layer: H21MapMarkerClusterDirective): Observable<D> {
        return new Observable((observer: Observer<any>) => {
            this.createEvent('mouseover', layer).subscribe((event: any) => {
                this._zone.run(() => observer.next(this._createEventField(event)));
            });
        });
    }

    public createEventMouseOut<D>(layer: H21MapMarkerClusterDirective): Observable<D> {
        return new Observable((observer: Observer<any>) => {
            this.createEvent('mouseout', layer).subscribe((event: any) => {
                this._zone.run(() => observer.next(event));
            });
        });
    }

    public createEventMouseClick<DE>(layer: H21MapMarkerClusterDirective): Observable<DE> {
        return new Observable((observer: Observer<any>) => {
            this.createEvent('click', layer).subscribe((event: any) => {
                this._zone.run(() => observer.next(this._createEventField(event)));
                event.sourceTarget.feature.bbox && this._clusterFitbounds(event);
            });
        });
    }

    public setModeCircle(CircleOptions: CircleEvent): void {
        this.modeCircle.next(CircleOptions);
    }

    public setFilter(filter: MarkerClusterFilter): void {
        this.changeFilter.next(filter);
    }

    public updateStyle(id: number, position): void {
        this._redrawStyle();
        try {
            const layer = this.layers.get(this._layer) && this.layers.get(this._layer).getLayers().find((val) => val.feature.id === id);
            layer ? layer.setIcon(this._createOptions(layer.feature, true)) : this.isMarkerCluster.next(position);
        } catch { }
    }

    public clearMarkerCluster(): void {
        this.removeFeature(this._layer);
        this.clearMarkerCluster$.next();
    }

    public getBounds(): Observable<any> {
        try {
            const bounds = L.latLngBounds([null, null], [null, null]);
            return new Observable((observer: Observer<any>) => {
                this.layers.get(this._layer) && this.layers.get(this._layer).eachLayer((layer: any) => {
                    bounds.extend(layer.getLatLng());
                });
                observer.next(bounds);
            });
        } catch { }
    }

    private _createDataLayer(): Observable<L.GeoJSON> {
        return new Observable((observer: Observer<L.GeoJSON>) => {
            observer.next(new L.GeoJSON(null, {
                pointToLayer: (feature, latlng) => {
                    return this._createOverlay(feature, latlng);
                },
                style: (feature) => {
                    return this._addDataStyles(feature);
                },

                onEachFeature: (feature, layer) => {
                    this._overrideStyle(feature, layer);
                },

                filter: (feature: L.GeoJSON.Feature) => {
                    feature.type = 'Feature';
                    return true;
                },

            }));
        });
    }

    private _addDataStyles(feature: any, isSelected?: boolean): any {
        return {
            zIndexOffset: isSelected ?
                feature.properties.zIndexSelected
                : feature.properties.zIndex,
            riseOnHover: true,
            bubblingMouseEvents: true,
        };
    }

    private _createOverlay(feature: L.GeoJSON.Feature, LatLng?: L.LatLng): any {
        return L.marker(LatLng, { icon: this._createOptions(feature) });
    }

    private _redrawStyle() {
        try {
            this.layers.get(this._layer) && this.layers.get(this._layer).eachLayer((layer: any) => {
                if (layer.feature.number) { return; }
                layer.setIcon(this._createOptionsRedraw(layer.feature));
            });
        } catch { }
    }


    private _createOptionsRedraw(feature: any, isSelected?: boolean): any {
        const icon = isSelected ? feature.properties.iconClassSelected : feature.properties.iconClass;
        let iconSelected = feature.number;
        iconSelected ? iconSelected = `${feature.properties.iconClass}${' '}${feature.properties.iconClassSelected}`
            : iconSelected = feature.properties.iconUrlSelected;
        return L.divIcon({
            iconSize: feature.properties.iconSize ? new L.Point(feature.properties.iconSize[0], feature.properties.iconSize[1])
                : new L.Point(0, 0),
            iconAnchor: feature.properties.iconUrl ? new L.Point(12, 31) : new L.Point(60, 31),
            className: feature.properties.iconUrl ? 'leaflet-div-icon' : iconSelected || icon,
            html: feature.properties.iconUrl ? `<img src='${isSelected ? feature.properties.iconUrlSelected
                : feature.properties.iconUrl}'/>` : feature.properties.iconContent,
        });
    }


    private _createOptions(feature: any, isSelected?: boolean): any {
        const icon = isSelected ? feature.properties.iconClassSelected : feature.properties.iconClass;
        let iconSelected = feature.number;
        iconSelected ? iconSelected = `${feature.properties.iconClass}${' '}${feature.properties.iconClassSelected}`
            : iconSelected = feature.properties.iconUrlSelected;
        return L.divIcon({
            iconSize: feature.properties.iconSize ? new L.Point(feature.properties.iconSize[0], feature.properties.iconSize[1])
                : new L.Point(0, 0),
            iconAnchor: feature.properties.iconUrl ? new L.Point(12, 31) : new L.Point(60, 31),
            className: feature.properties.iconUrl ? 'leaflet-div-icon' : icon,
            html: feature.properties.iconUrl ? `<img src='${isSelected ? feature.properties.iconUrlSelected
                : feature.properties.iconUrl}'/>` : feature.properties.iconContent,
        });
    }

    private _removeFeature(layer: H21MapMarkerClusterDirective): void {
        try {
            this.layers.get(layer) && this.layers.get(layer).clearLayers();
        } catch { }
    }

    private _createEventField(event: any): FeatureEvent {
        !event.sourceTarget.feature.properties.iconUrl && this._eventPosition(event);
        const pixel = this.map.conversions.translateLatLngClientXY(event);
        const latlng = event.latlng;
        return new FeatureEvent(
            new Position(latlng.lat, latlng.lng),
            pixel.clientX,
            pixel.clientY,
            event.sourceTarget.feature.properties.data || null,
            event.sourceTarget.feature.number ? 'Cluster' : 'Feature',
        );
    }

    private _overrideStyle(feature, layer) {
        layer.on('mouseover', () => {
            if (feature.number) { return; }
            layer.setIcon(this._createOptions(feature, true));
            layer.setZIndexOffset(feature.properties.zIndexSelected || 1000);
        });
        layer.on('mouseout', () => {
            layer.setZIndexOffset(feature.properties.zIndex || 100);
            layer.setIcon(this._createOptions(feature));
        });
    }

    private _eventPosition(event: any) {
        const size = event.sourceTarget._icon;
        event.offsetX = size.offsetWidth / 2 - 60;
        event.offsetY = size.offsetHeight;
        return event;
    }

    private _clusterFitbounds(event: any): void {
        const bbox = event.sourceTarget.feature.bbox;
        const bounds = L.latLngBounds([bbox[0][1], bbox[0][0]], [bbox[1][1], bbox[1][0]]);
        this.map.api.flyToBounds(bounds);
    }

}
